/**
 * IONLOOP Assistant — deterministic sentence selector.
 *
 * Picks the most relevant, source-backed sentences from a knowledge section
 * without rewriting or inventing content. Sentence order is preserved when
 * scores are reasonably close.
 */

var tokenizer = require('../knowledge/tokenizer');

var ABBREVIATIONS = /\b(?:mr|mrs|ms|dr|prof|sr|jr|st|vs|etc|e\.g|i\.e|approx|inc|ltd|no|jan|feb|mar|apr|jun|jul|aug|sep|oct|nov|dec)\.$/i;

/**
 * Split English prose into sentences.
 */
function splitSentences(text) {
    if (!text) {
        return [];
    }
    var raw = String(text);
    var sentences = [];
    var buffer = '';
    var i, ch, next;
    for (i = 0; i < raw.length; i += 1) {
        ch = raw[i];
        next = raw[i + 1];
        buffer += ch;
        if ((ch === '.' || ch === '!' || ch === '?') &&
            (next === undefined || next === ' ' || next === '\n' || next === '\r')) {
            var trimmed = buffer.trim();
            if (trimmed) {
                sentences.push(trimmed);
            }
            buffer = '';
        }
    }
    var rest = buffer.trim();
    if (rest) {
        sentences.push(rest);
    }
    return sentences;
}

/**
 * Score a sentence against the query's meaningful terms and the section's
 * heading terms. Returns a numeric relevance score.
 */
function scoreSentence(sentence, meaningfulTerms, headingTokens) {
    var lower = sentence.toLowerCase();
    var score = 0;
    var tokens = tokenizer.tokenize(lower);
    var matched = {};

    meaningfulTerms.forEach(function (term) {
        var key = term;
        if (matched[key]) {
            return;
        }
        if (lower.indexOf(' ' + term + ' ') !== -1 || lower.indexOf(term) === 0) {
            score += 2;
            matched[key] = true;
        } else if (tokens.indexOf(term) !== -1) {
            score += 2;
            matched[key] = true;
        }
    });

    // Heading terms in the sentence indicate a definitional sentence.
    headingTokens.forEach(function (tok) {
        if (tokens.indexOf(tok) !== -1 && !matched[tok]) {
            score += 1;
            matched[tok] = true;
        }
    });

    // Penalize generic / empty-sounding sentences.
    if (/^(there|it|this|that|these|those|the|a|an)\s/i.test(sentence)) {
        score -= 0.5;
    }
    if (tokens.length < 3) {
        score -= 1;
    }
    return score;
}

/**
 * Select the best 1..maxSentences sentences from a section's text, keeping
 * source order. Returns array of sentence strings.
 */
function selectSentences(sectionText, meaningfulTerms, headingText, options) {
    options = options || {};
    var maxSentences = options.maxSentences || 3;
    var minScore = options.minScore !== undefined ? options.minScore : 0.5;

    var sentences = splitSentences(sectionText);
    if (sentences.length === 0) {
        return [];
    }

    var headingTokens = tokenizer.tokenize(headingText || '');
    var scored = sentences.map(function (sentence) {
        return {
            sentence: sentence,
            score: scoreSentence(sentence, meaningfulTerms, headingTokens)
        };
    });

    scored.sort(function (a, b) { return b.score - a.score; });

    var picked = scored
        .filter(function (item) { return item.score >= minScore; })
        .slice(0, maxSentences)
        .sort(function (a, b) { return sentences.indexOf(a.sentence) - sentences.indexOf(b.sentence); })
        .map(function (item) { return item.sentence; });

    // Fallback: if nothing passed the threshold, take the first substantial
    // sentence (source-backed, not fabricated).
    if (picked.length === 0) {
        var fallback = sentences.find(function (s) { return s.length >= 40; });
        if (fallback) {
            picked = [fallback];
        }
    }

    return picked;
}

/**
 * Conservative near-duplicate detection for answer components.
 * Returns true when two strings are effectively the same information.
 */
function isNearDuplicate(a, b) {
    var normA = String(a || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
    var normB = String(b || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
    if (!normA || !normB) {
        return false;
    }
    var longer = normA.length >= normB.length ? normA : normB;
    var shorter = normA.length >= normB.length ? normB : normA;
    return longer.indexOf(shorter) !== -1 && shorter.length >= 40;
}

module.exports = {
    splitSentences: splitSentences,
    scoreSentence: scoreSentence,
    selectSentences: selectSentences,
    isNearDuplicate: isNearDuplicate
};
